import { Button } from "@/components/ui/button";
import { IncomeSection } from "./IncomeSection";
import { HousingSection } from "./HousingSection";
import { ExpensesSection } from "./ExpensesSection";
import { SavingsSection } from "./SavingsSection";
import { useEffect, useMemo, useState, type Reducer } from "react";
import { SummarySection } from "./SummarySection";
import {
  defaultExpenseData,
  defaultIncomeData,
  defaultMortgageData,
  initialExpensesData,
  initialIncomeData,
  initialMortgageData,
  type ExpenseType,
} from "./data";
import {
  calculateTotalExpensesPerYear,
  calculateTotalIncomePerYear,
  calculateMortgageCostPerYear,
} from "./lib/calculators";
import {
  useCrudReducerNested,
  useCrudReducerFlat,
} from "./hooks/useCrudReducer";
import { useLocalStorage } from "@uidotdev/usehooks";

export function ExpensesAndSavings() {
  const [localStorageIncomeData, setLocalStorageIncomeData] = useLocalStorage(
    "money-power-app-income-data",
    initialIncomeData
  );
  // Want to decouple setting local storage data and current data in app
  const startingIncomeData = useMemo(() => {
    return localStorageIncomeData.map((data) => ({
      ...defaultIncomeData(),
      ...data,
    }));
  }, []);

  const [localStorageMortgageData, setLocalStorageMortgageData] =
    useLocalStorage("money-power-app-mortgage-data", initialMortgageData);
  const startingMortgageData = useMemo(() => {
    return localStorageMortgageData.map((data) => ({
      ...defaultMortgageData(),
      ...data,
    }));
  }, []);

  const [localStorageExpensesData, setLocalStorageExpensesData] =
    useLocalStorage("money-power-app-expenses-data", initialExpensesData);
  // Make sure any subsections not already in local storage are included
  const startingExpensesData = useMemo(() => {
    // Make sure no fields are missing between potentially old storage data
    // expected data schema
    const normalisedStorageExpensesData = Object.fromEntries(Object.entries(
      localStorageExpensesData
    ).map(([type, data]) => [
      type,
      data.map((val) => ({ ...defaultExpenseData(), ...val })),
    ]));
    return { ...initialExpensesData, ...normalisedStorageExpensesData };
  }, []);

  const {
    data: incomeData,
    handleCreate: handleCreateIncome,
    handleUpdate: handleUpdateIncome,
    handleDelete: handleDeleteIncome,
  } = useCrudReducerFlat(startingIncomeData);

  const {
    data: mortgageData,
    handleCreate: handleCreateMortage,
    handleUpdate: handleUpdateMortgage,
    handleDelete: handleDeleteMortgage,
  } = useCrudReducerFlat(startingMortgageData);

  const {
    data: expensesData,
    handleCreate: handleCreateExpense,
    handleUpdate: handleUpdateExpense,
    handleDelete: handleDeleteExpense,
  } = useCrudReducerNested(startingExpensesData);

  useEffect(() => {
    setLocalStorageIncomeData(incomeData);
  }, [incomeData]);
  useEffect(() => {
    setLocalStorageMortgageData(mortgageData);
  }, [mortgageData]);
  useEffect(() => {
    setLocalStorageExpensesData(expensesData);
  }, [expensesData]);

  const totalIncomePerYear = useMemo(() => {
    return calculateTotalIncomePerYear(incomeData);
  }, [incomeData]);

  const totalMortgageCostPerYear = useMemo(() => {
    return mortgageData
      .map((data) => {
        if (data.selected) {
          const {
            loanAmount,
            interestRate,
            loanTerm,
            interestOnly,
            repaymentFrequency,
          } = data;
          const repayment = calculateMortgageCostPerYear({
            loanAmount,
            loanTerm,
            interestRate: interestRate / 100,
            interestOnly,
            repaymentFrequency,
          });
          return repayment;
        } else {
          return 0;
        }
      })
      .reduce((prev, current) => prev + current, 0);
  }, [mortgageData]);

  const [totalEssentialExpensesPerYear, totalNonEssentialExpensesPerYear] =
    useMemo(() => {
      const expensesTotals = Object.entries(expensesData).reduce(
        (acc, [key, data]) => {
          return {
            ...acc,
            [key]: calculateTotalExpensesPerYear(data),
          };
        },
        {} as Record<
          ExpenseType,
          ReturnType<typeof calculateTotalExpensesPerYear>
        >
      );
      return [
        Object.values(expensesTotals).reduce(
          (prev, val) => prev + val.essential,
          0
        ),
        Object.values(expensesTotals).reduce(
          (prev, val) => prev + val.nonEssential,
          0
        ),
      ];
    }, [expensesData]);

  // TODO: Add tooltips

  return (
    <div className="w-full flex flex-col gap-4">
      <SummarySection
        totalEssentialExpenses={totalEssentialExpensesPerYear}
        totalIncome={totalIncomePerYear}
        totalMortgageCost={totalMortgageCostPerYear}
        totalNonEssentialExpenses={totalNonEssentialExpensesPerYear}
      />
      <IncomeSection
        incomeData={incomeData}
        onCreate={handleCreateIncome}
        onUpdate={handleUpdateIncome}
        onDelete={handleDeleteIncome}
      />
      <HousingSection
        mortgageData={mortgageData}
        onCreate={handleCreateMortage}
        onUpdate={handleUpdateMortgage}
        onDelete={handleDeleteMortgage}
      />
      <ExpensesSection
        expensesData={expensesData}
        onCreate={handleCreateExpense}
        onUpdate={handleUpdateExpense}
        onDelete={handleDeleteExpense}
      />
    </div>
  );
}
